import React, { useEffect } from "react";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { ScheduleDateType } from "../scheduledDateDropdown/scheduledDateDropdown";
import { DateTime } from "luxon";
import { OrderEstimates } from "@/dto/orderEstimates";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { OrderType } from "@/types";
import {
  HHMMtoDate,
  roundToNext15Minutes,
  daysOfWeek,
} from "@/utils/dateTimeUtils";
import { DayPart } from "@/dto/dayPartMenu";
import { getBusinessHoursOfDay } from "@/utils/scheduleOrderUtils";
import { useTranslations } from "next-intl";

export type ScheduleTimeType = {
  displayValue: string;
  timeObject: Date;
};

export type ScheduleTime = {
  orderType: string;
  date: ScheduleDateType;
  time: ScheduleTimeType;
};

export const ScheduledTimeDropdown = ({
  scheduleTime,
  setScheduleTime,
  businessHours,
  scheduleDate,
  leadTime,
  timezone,
  orderType,
  selectedDayPart = null,
}: {
  scheduleTime: ScheduleTimeType;
  // eslint-disable-next-line no-unused-vars
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  businessHours: any;
  scheduleDate: ScheduleDateType;
  leadTime: any;
  timezone: string;
  orderType: string;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("scheduledTimeDropdown");
  const nowInMerchantZone = DateTime.now().setZone(timezone);
  const timeOptions: any[] = [];
  const day = DateTime.fromJSDate(scheduleDate?.dateObject, {
    zone: timezone,
  }).weekday;
  const dayName = daysOfWeek[day % 7];
  const { deliveryTime, pickupTime } = getFromSessionStorage<
    Partial<OrderEstimates>
  >(SessionStorageKey.ORDER_ESTIMATES, {});

  const generateTimeOptions = (
    timeRanges: { start: string; end: string }[],
    date: Date
  ) => {
    const currentDayInMerchantZone = DateTime.fromJSDate(date, {
      zone: timezone,
    });
    const deliveryTimeDate = deliveryTime
      ? new Date(deliveryTime).getTime()
      : 0;
    const pickUpTimeDate = pickupTime ? new Date(pickupTime).getTime() : 0;
    const deliveryDelay =
      orderType === OrderType.DELIVERY
        ? Math.round((deliveryTimeDate - pickUpTimeDate) / 1000 / 60)
        : 0;

    // This happens when order type is changed to delivery but delivery address is not entered
    // if (!deliveryDelay) {
    //   return;
    // }

    timeRanges.forEach(({ start, end }) => {
      let startTime = HHMMtoDate(start.padStart(4, "0"), timezone, date);
      const endTime = HHMMtoDate(end.padStart(4, "0"), timezone, date);

      /**
       * BUG:
       * Lets take a case where
       * Start Time: 8:00 AM
       * Lead Time: 60 mins
       * Current Time is 8:45 AM
       * LeadTimeThreshold will be 9:00 AM which is greater than current time
       * So it will show 9:15AM as scheduled time where as food preperation time + current time will come to 9:45AM
       */
      let leadTimeThreshold = roundToNext15Minutes(
        startTime.plus({ minutes: leadTime + deliveryDelay })
      );

      if (
        currentDayInMerchantZone.hasSame(nowInMerchantZone, "day") &&
        leadTimeThreshold < nowInMerchantZone
      ) {
        leadTimeThreshold = roundToNext15Minutes(
          nowInMerchantZone.plus({ minutes: leadTime + deliveryDelay })
        );
      }

      for (
        let time = leadTimeThreshold;
        time <= endTime;
        time = time.plus({ minutes: 15 })
      ) {
        timeOptions.push({
          displayValue: time.toLocaleString(DateTime.TIME_SIMPLE),
          timeObject: time.toJSDate(),
        });
      }
    });
  };

  if (businessHours && businessHours[dayName]) {
    const todayTimeRanges = getBusinessHoursOfDay(
      businessHours,
      dayName,
      selectedDayPart
    );
    generateTimeOptions(todayTimeRanges, scheduleDate?.dateObject);
  }

  useEffect(() => {
    if (timeOptions.length > 0 && !scheduleTime?.displayValue) {
      setScheduleTime(timeOptions[0]);
    }
  }, [scheduleDate, orderType]);

  return (
    <div
      key={
        scheduleDate?.dateObject &&
        !isNaN(new Date(scheduleDate.dateObject).getTime())
          ? new Date(scheduleDate.dateObject).toISOString()
          : "no-date"
      }
    >
      <Popover className="relative">
        <PopoverButton
          className={[
            "flex",
            "items-center",
            "gap-x-2",
            "w-full",
            "leading-6",
            "text-gray-900",
            "hover:bg-gray-50",
          ].join(" ")}
        >
          <input
            type="text"
            id="scheduled_time_drop"
            className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border border-black hover:border-black focus:outline-none focus:ring-1 focus:ring-blue peer cursor-pointer"
            value={scheduleTime.displayValue}
            readOnly
          />
          <label
            htmlFor="scheduled_time_drop"
            className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
          >
            {t("time")}
          </label>
        </PopoverButton>
        <PopoverPanel
          transition
          className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
          style={{ maxHeight: "400px", overflowY: "auto" }}
        >
          <div className="p-3">
            {timeOptions.map((time) => (
              <CloseButton
                key={time.displayValue}
                className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 flex-auto cursor-pointer"
                onClick={() => setScheduleTime(time)}
              >
                <p className="text-[15px]">{time.displayValue}</p>
              </CloseButton>
            ))}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
};
